import { AfterViewChecked, Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { of, Subject, switchMap, takeUntil } from 'rxjs';
import { StorageService } from 'src/app/_services/storage.service';
import { ApiExplorerService } from '../api-explorer-service';
import { API_PAGE_CONFIGS } from './api-explorer.config';
import { mergeApiExplorerMenu } from './api-explorer.menu';
import { environment } from 'src/environments/environment';
import { PaymentGatewayProxyService } from 'src/app/_services/pg-proxy.service';
import { AuthService } from 'src/app/_services/auth.service';
import { Role } from 'src/app/_helpers/auth.constants';

const { API_EXP_SANDBOX_URL, API_EXP_API_URL, WALLET_API_EXP_SANDBOX_URL, WALLET_API_EXP_API_URL } = environment;

@Component({
  selector: 'app-api-explorer',
  templateUrl: './api-explorer.component.html',
  styleUrls: ['./api-explorer.component.css']
})

export class APIExplorerComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChildren('autoTextarea') autoTextareas!: QueryList<ElementRef<HTMLTextAreaElement>>;

  isLoggedIn: boolean = false;

  selectedApi: string | null = null;
  apiDetails: any = null;
  isLoadingApiDetails = false;
  apiDetailsError: string | null = null;
  isSampleRequestOpen = true;
  isResponseOpen = true;
  responseItemOpen: { [key: string]: boolean } = {};
  isCurlRequestOpen = true;
  curlFormat: 'curl' | 'httpie' = 'curl';
  isCurlFormatDropdownOpen = false;
  isSampleResponseOpen = true;
  copiedCurl = false;
  copiedStaticCurl = false;
  copiedRunResult = false;
  copiedSampleSuccess = false;
  copiedSampleFailure = false;
  paramValues: { [key: string]: string } = {};
  runResult: { status: number; statusText: string; body: any } | null = null;
  isRunning = false;
  bodyValues: { [key: string]: any } = {};
  /** Selected files for multipart endpoints (keyed by field name). */
  fileValues: { [key: string]: File | null } = {};
  accessToken = '';
  prevApiItem: { label: string; apiKey: string } | null = null;
  nextApiItem: { label: string; apiKey: string } | null = null;
  merchantId = '';
  role: string = '';
  Role = Role;

  private readonly destroy$ = new Subject<void>();



  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly storageService: StorageService,
    private readonly apiExplorerService: ApiExplorerService,
    private readonly sanitizer: DomSanitizer,
    private readonly paymentGatewayProxyService: PaymentGatewayProxyService,
    private readonly authService: AuthService
  ) {
    this.isLoggedIn = this.storageService.isLoggedIn();
  }

  ngOnInit(): void {
    this.merchantId = localStorage.getItem('merchantId') || '';
    this.role = this.authService.getRole();
    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        this.selectedApi = params['api'] || null;
        if (this.selectedApi) {
          this.loadApiData(this.selectedApi);
        } else {
          this.loadDefaultApiExplorer();
        }
      });
  }

  navigateToFirstMenuItem(): void {
    const items = this.sortedMenuItems;
    if (!items.length) return;
    const target = items.find(item => item?.nodeInfo?.menuLink);
    if (target) {
      localStorage.setItem('selectedMenu', target.nodeName);
      this.router.navigate([target.nodeInfo.menuLink], { queryParams: target.nodeInfo.queryParams || {} });
    }
  }

  get sortedMenuItems(): any[] {
    const data = localStorage.getItem('menuItemsFormattedApiExplorer');
    const items: any[] = mergeApiExplorerMenu(data ? JSON.parse(data) : []);
    const parents = items
      .filter(item => item.nodeInfo?.type === 'menu')
      .sort((a, b) => Number(a.nodeInfo.position) - Number(b.nodeInfo.position));
    const result: any[] = [];
    parents.forEach(parent => {
      const children = items
        .filter(item => item.parentId === parent.nodeId && item.nodeInfo?.menuLink)
        .sort((a, b) => Number(a.nodeInfo.position) - Number(b.nodeInfo.position));
      result.push(...children);
    });
    return result;
  }

  docId: string = '';
  get currentMenuIndex(): number {
    return this.sortedMenuItems.findIndex(
      item => item.nodeInfo?.queryParams?.api === this.docId
    );
  }

  get prevMenuItem(): any | null {
    const items = this.sortedMenuItems;
    const idx = this.currentMenuIndex;
    if (idx <= 0) return null;
    for (let i = idx - 1; i >= 0; i--) {
      if (items[i]?.nodeInfo?.menuLink) return items[i];
    }
    return null;
  }

  get nextMenuItem(): any | null {
    const items = this.sortedMenuItems;
    const idx = this.currentMenuIndex;
    if (idx < 0) return null;
    for (let i = idx + 1; i < items.length; i++) {
      if (items[i]?.nodeInfo?.menuLink) return items[i];
    }
    return null;
  }

  navigateToPrev(): void {
    const item = this.prevMenuItem;
    if (!item) return;
    localStorage.setItem('selectedMenu', item.nodeName);
    this.router.navigate([item.nodeInfo.menuLink], { queryParams: item.nodeInfo.queryParams || {} });
    document.querySelector('.api-doc-page')?.scrollTo({ top: 0, behavior: 'smooth' });
  }

  navigateToNext(): void {
    const item = this.nextMenuItem;
    if (!item) return;
    localStorage.setItem('selectedMenu', item.nodeName);
    this.router.navigate([item.nodeInfo.menuLink], { queryParams: item.nodeInfo.queryParams || {} });
    document.querySelector('.api-doc-page')?.scrollTo({ top: 0, behavior: 'smooth' });
  }


  private loadApiData(apiName: string): void {
    this.docId = apiName;
    this.isLoadingApiDetails = true;
    this.apiDetails = null;
    this.apiDetailsError = null;
    this.paramValues = {};
    this.bodyValues = {};
    this.fileValues = {};
    this.runResult = null;
    this.responseItemOpen = {};

    const config = API_PAGE_CONFIGS[apiName] ?? API_PAGE_CONFIGS['_generic'];
    this.apiDetails = config;

    // Pre-populate body values with example values from config
    const bodyFields = config?.endpoints?.[0]?.requestBodyFields;
    if (bodyFields?.length) {
      bodyFields.forEach((f: any) => {
        if (f.schema?.example !== undefined) {
          this.bodyValues[f.name] = f.schema.example;
        }
      });

      // Override PAY_ID with actual merchantId
      if (this.merchantId && bodyFields.some((f: any) => f.name === 'PAY_ID')) {
        this.bodyValues['PAY_ID'] = this.merchantId;
      }
    }

    this.isLoadingApiDetails = false;
  }

  private loadDefaultApiExplorer(): void {
    this.apiDetails = null;
    this.apiDetailsError = null;
    this.isLoadingApiDetails = false;
  }

  navigateToSignup(): void {
    this.router.navigate(['/auth/login']);
  }

  toggleSampleRequest(): void {
    this.isSampleRequestOpen = !this.isSampleRequestOpen;
  }

  toggleResponse(): void {
    this.isResponseOpen = !this.isResponseOpen;
  }

  toggleResponseItem(status: string): void {
    this.responseItemOpen[status] = !this.isResponseItemOpen(status);
  }

  hasRefundField(response: any): boolean {
    return response?.responseFields?.some((f: any) => f.name?.trim() === 'REFUND_ORDER_ID');
  }

  isResponseItemOpen(status: string): boolean {
    return this.responseItemOpen[status] === true;
  }

  toggleCurlRequest(): void {
    this.isCurlRequestOpen = !this.isCurlRequestOpen;
  }

  toggleFormatDropdown(): void {
    this.isCurlFormatDropdownOpen = !this.isCurlFormatDropdownOpen;
  }

  highlightCode(content: string, lang: 'curl' | 'httpie'): SafeHtml {
    const escaped = content
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    let highlighted: string;
    if (lang === 'curl') {
      highlighted = escaped.replace(
        /(\bcurl\b)|(--[\w-]+)|('(?:[^'\\]|\\.)*')|(\\(?=\n|$))/gm,
        (m: string, cmd: string, flag: string, str: string, bs: string) => {
          if (cmd) return `<span class="hl-cmd">${cmd}</span>`;
          if (flag) return `<span class="hl-flag">${flag}</span>`;
          if (str) return `<span class="hl-str">${str}</span>`;
          if (bs) return `<span class="hl-bs">${bs}</span>`;
          return m;
        }
      );
    } else {
      highlighted = escaped.replace(
        /(\bhttp\b)|\b(POST|GET|PUT|DELETE|PATCH)\b|('(?:[^'\\]|\\.)*')|(\\(?=\n|$))|([\w.-]+?)(:=|==|=)/gm,
        (m: string, cmd: string, method: string, str: string, bs: string, key: string, op: string) => {
          if (cmd) return `<span class="hl-cmd">${cmd}</span>`;
          if (method) return `<span class="hl-method">${method}</span>`;
          if (str) return `<span class="hl-str">${str}</span>`;
          if (bs) return `<span class="hl-bs">${bs}</span>`;
          if (key && op) return `<span class="hl-key">${key}</span><span class="hl-flag">${op}</span>`;
          return m;
        }
      );
    }
    return this.sanitizer.bypassSecurityTrustHtml(highlighted);
  }

  setCurlFormat(format: 'curl' | 'httpie'): void {
    this.curlFormat = format;
    this.isCurlFormatDropdownOpen = false;
  }

  buildHttpieCommand(endpoint: any): string {
    const pathParams = this.filterParams(endpoint.parameters, 'path');
    const queryParams = this.filterParams(endpoint.parameters, 'query');
    const headerParams = this.filterParams(endpoint.parameters, 'header');

    let path = endpoint.path;
    pathParams.forEach((p: any) => {
      const val = this.paramValues[p.name] || '{' + p.name + '}';
      path = path.replace('{' + p.name + '}', val);
    });

    const queryArgs = queryParams
      .filter((p: any) => p.required || this.paramValues[p.name])
      .map((p: any) => '  ' + p.name + '==' + (this.paramValues[p.name] || ''));

    const url = (endpoint.isFromPG ? API_EXP_SANDBOX_URL : WALLET_API_EXP_SANDBOX_URL) + path;
    const method = (endpoint.method as string).toUpperCase();

    const parts: string[] = [
      'http ' + method + " '" + url + "'",
    ];

    headerParams.forEach((p: any) => {
      const isAuth = p.name.toLowerCase() === 'authorization';
      const userVal = this.paramValues[p.name];
      if (!isAuth && !p.required && !userVal) return;
      let val: string;
      if (isAuth) {
        val = userVal || (this.accessToken ? 'Bearer ' + this.accessToken : 'Bearer <access_token>');
      } else {
        val = userVal || '';
      }
      parts.push(`  '${p.name}:${val}'`);
    });

    queryArgs.forEach(q => parts.push(q));

    if (endpoint.requestBodyFields?.length && ['POST', 'PUT', 'PATCH'].includes(method)) {
      this.visibleBodyFields(endpoint).forEach((f: any) => {
        const val = this.bodyValues[f.name];
        const hasVal = val !== undefined && val !== null && val !== '';
        if (!f.required && !hasVal) return;
        const isNonString = f.schema?.type === 'integer' || f.schema?.type === 'boolean' || f.schema?.type === 'number';
        parts.push(`  ${f.name}${isNonString ? ':=' : '='}${hasVal ? val : "''"}`);
      });
    }

    return parts.join(' \\\n');
  }

  toggleSampleResponse(): void {
    this.isSampleResponseOpen = !this.isSampleResponseOpen;
  }

  copySampleSuccess(content: any): void {
    navigator.clipboard.writeText(JSON.stringify(content, null, 2));
    this.copiedSampleSuccess = true;
    setTimeout(() => this.copiedSampleSuccess = false, 2000);
  }

  copySampleFailure(content: any): void {
    navigator.clipboard.writeText(JSON.stringify(content, null, 2));
    this.copiedSampleFailure = true;
    setTimeout(() => this.copiedSampleFailure = false, 2000);
  }

  isMultipartEndpoint(endpoint: any): boolean {
    return endpoint?.requestContentType === 'multipart/form-data';
  }

  isBinaryResponseEndpoint(endpoint: any): boolean {
    return endpoint?.responseType === 'blob'
      || endpoint?.successResponse?.contentType === 'XLSX'
      || endpoint?.successResponse?.contentType === 'binary';
  }

  onFileSelected(fieldName: string, event: Event): void {
    const input = event.target as HTMLInputElement;
    this.fileValues[fieldName] = input.files?.[0] ?? null;
  }

  private buildMultipartBody(endpoint: any): FormData | null {
    const formData = new FormData();
    let hasFile = false;
    (endpoint?.requestBodyFields || []).forEach((f: any) => {
      if (f.schema?.type === 'file' || f.schema?.format === 'binary') {
        const file = this.fileValues[f.name];
        if (file) {
          formData.append(f.name, file, file.name);
          hasFile = true;
        }
      } else {
        const val = this.bodyValues[f.name];
        if (val !== undefined && val !== null && val !== '') {
          formData.append(f.name, String(val));
        }
      }
    });
    return hasFile ? formData : null;
  }

  private triggerBlobDownload(blob: Blob, fileName: string): void {
    // Same pattern as Merchant Portal payout template download
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName || 'bulk_payout_template.xlsx';
    a.click();
    URL.revokeObjectURL(url);
  }

  runApi(): void {
    this.isRunning = true;
    this.runResult = null;

    const endpoint = this.apiDetails?.endpoints?.[0];
    if (!endpoint || !this.hasCallableEndpoint) {
      this.isRunning = false;
      return;
    }

    const method = (endpoint.method as string).toUpperCase();
    const isMultipart = this.isMultipartEndpoint(endpoint);
    const wantsBlob = this.isBinaryResponseEndpoint(endpoint);

    // Resolve path params
    let path = endpoint.path;
    this.filterParams(endpoint.parameters, 'path').forEach((p: any) => {
      path = path.replace('{' + p.name + '}', this.paramValues[p.name] ?? '');
    });

    const url = (endpoint.isFromPG ? API_EXP_SANDBOX_URL : WALLET_API_EXP_SANDBOX_URL) + path;

    // Build headers — skip optional ones with no user value (Authorization always included)
    const headers: { [key: string]: string } = {};
    this.filterParams(endpoint.parameters, 'header').forEach((p: any) => {
      const isAuth = p.name.toLowerCase() === 'authorization';
      const val = this.paramValues[p.name];
      if (isAuth) {
        const authVal = val || (this.accessToken ? 'Bearer ' + this.accessToken : '');
        if (authVal) headers[p.name] = authVal;
      } else if (val) {
        headers[p.name] = val;
      }
    });

    // Build query params
    const queryParams: { [key: string]: string } = {};
    this.filterParams(endpoint.parameters, 'query').forEach((p: any) => {
      if (this.paramValues[p.name]) queryParams[p.name] = this.paramValues[p.name];
    });

    // Build body (null when no request body fields); multipart uses FormData
    const body = isMultipart ? this.buildMultipartBody(endpoint) : this.buildRequestBody(endpoint);

    if (isMultipart && !body) {
      this.runResult = {
        status: 400,
        statusText: 'Bad Request',
        body: { message: 'Please choose a file to upload before running this request.' }
      };
      this.isRunning = false;
      return;
    }

    const prepare$ = isMultipart
      ? of(body)
      : this.paymentGatewayProxyService.prepareRequest(this.docId, (body ?? {}) as Record<string, unknown>);

    prepare$
      .pipe(
        switchMap(preparedBody => this.apiExplorerService.callApi({
          url,
          method,
          headers,
          queryParams,
          body: preparedBody,
          isMultipart,
          responseType: wantsBlob ? 'blob' : 'json'
        })),
        switchMap(response => this.paymentGatewayProxyService.processResponse(
          this.docId,
          response
        )),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: res => {
          if ((res as any).blob instanceof Blob) {
            this.triggerBlobDownload((res as any).blob, (res as any).fileName || 'download.bin');
          }
          this.runResult = res;
          this.isRunning = false;
        },
        error: err => {
          this.runResult = {
            status: err?.status ?? 500,
            statusText: err?.statusText ?? 'Error',
            body: err?.error ?? err
          };
          this.isRunning = false;
        }
      });
  }

  copyRunResult(): void {
    if (!this.runResult) return;
    navigator.clipboard.writeText(JSON.stringify(this.runResult.body, null, 2));
    this.copiedRunResult = true;
    setTimeout(() => this.copiedRunResult = false, 2000);
  }

  copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text).then(() => {
      this.copiedCurl = true;
      setTimeout(() => { this.copiedCurl = false; }, 2000);
    });
  }

  copyStaticCurl(text: string): void {
    navigator.clipboard.writeText(text).then(() => {
      this.copiedStaticCurl = true;
      setTimeout(() => { this.copiedStaticCurl = false; }, 2000);
    });
  }

  buildStaticCurlCommand(endpoint: any): string {
    const pathParams = this.filterParams(endpoint.parameters, 'path');
    const queryParams = this.filterParams(endpoint.parameters, 'query');
    const headerParams = this.filterParams(endpoint.parameters, 'header');
    const isMultipart = this.isMultipartEndpoint(endpoint);

    let path = endpoint.path;
    pathParams.forEach((p: any) => {
      path = path.replace('{' + p.name + '}', p.schema?.example ?? '{' + p.name + '}');
    });

    const queryString = queryParams
      .filter((p: any) => p.required || p.schema?.example)
      .map((p: any) => p.name + '=' + (p.schema?.example ?? ''))
      .join('&');
    const url = (endpoint.isFromPG ? API_EXP_SANDBOX_URL : WALLET_API_EXP_SANDBOX_URL) + path + (queryString ? '?' + queryString : '');
    const method = (endpoint.method as string).toUpperCase();

    const parts: string[] = [
      "curl --location '" + url + "'",
      "  --request " + method,
    ];
    if (!isMultipart) {
      parts.push("  --header 'Content-Type: application/json'");
    }

    headerParams.forEach((p: any) => {
      if (!p.required && !p.schema?.example) return;
      parts.push("  --header '" + p.name + ": " + (p.schema?.example ?? '{' + p.name + '}') + "'");
    });

    if (endpoint.requestBodyFields?.length && ['POST', 'PUT', 'PATCH'].includes(method)) {
      if (isMultipart) {
        this.visibleBodyFields(endpoint).forEach((f: any) => {
          if (f.schema?.type === 'file' || f.schema?.format === 'binary') {
            parts.push("  --form '" + f.name + "=@/" + (f.schema?.example || 'bulk_payout.xlsx') + "'");
          } else if (f.required || f.schema?.example !== undefined) {
            parts.push("  --form '" + f.name + "=" + (f.schema?.example ?? '') + "'");
          }
        });
      } else {
        const body: any = {};
        this.visibleBodyFields(endpoint).forEach((f: any) => {
          if (f.required || f.schema?.example !== undefined) body[f.name] = f.schema?.example ?? '';
        });
        if (Object.keys(body).length) parts.push("  --data-raw '" + JSON.stringify(body, null, 2) + "'");
      }
    }

    return parts.join(' \\\n');
  }

  /**
   * Payout doc ids resolve to MerchantPortal, which the Developer Portal reaches through
   * its proxy (/developer-portal/proxy/merchant-portal). Everything else keeps the
   * existing payment-gateway host.
   */
  get isWebhookDoc(): boolean {
    return !!this.apiDetails?.endpoints?.length && !this.hasCallableEndpoint;
  }

  get hasCallableEndpoint(): boolean {
    const endpoint = this.apiDetails?.endpoints?.[0];
    return !!endpoint && !!endpoint.method && !!endpoint.path;
  }

  /**
   * Key used to bind a request-body input. Nested fields are addressed by path so that a
   * batch item reads as `transfers[0].amount`. Arrays are documented and executed with a
   * single representative item.
   */
  bodyKey(prefix: string, f: any): string {
    const base = prefix ? prefix + '.' + f.name : f.name;
    return this.isArrayField(f) ? base + '[0]' : base;
  }

  private isArrayField(f: any): boolean {
    return typeof f?.schema?.type === 'string' && f.schema.type.startsWith('array');
  }

  bodyFieldType(f: any): string {
    const t = f.schema?.type;
    if (!t) return '-';
    if (t === 'file' || f.schema?.format === 'binary') return 'file';
    if (this.isArrayField(f)) return f.schema?.fields?.length ? 'array<object>' : 'array';
    return t;
  }

  private coerceBodyValue(val: any, f: any): any {
    const t = f.schema?.type;
    if (t === 'boolean') return val === true || val === 'true';
    if (t === 'integer' || t === 'number') {
      const n = Number(val);
      return isNaN(n) ? val : n;
    }
    return val;
  }

  /**
   * Assembles a request body from `requestBodyFields`, recursing into nested object and
   * array fields. `useExamples` drives the static Sample Request; otherwise the values the
   * user typed are used. Optional fields with no value are omitted.
   */
  private collectBody(fields: any[], prefix: string, useExamples: boolean): any {
    const out: any = {};
    (fields || []).forEach((f: any) => {
      const key = this.bodyKey(prefix, f);
      const children = f.schema?.fields;

      if (children?.length) {
        // Object/array fields are edited as raw JSON in a textarea bound to the plain path.
        const rawKey = prefix ? prefix + '.' + f.name : f.name;
        const rawJson = useExamples ? f.schema?.example : this.bodyValues[rawKey] ?? f.schema?.example;
        if (rawJson !== undefined && rawJson !== null && rawJson !== '') {
          out[f.name] = this.isArrayField(f) && !Array.isArray(rawJson) ? [rawJson] : rawJson;
          return;
        }
        const inner = this.collectBody(children, key, useExamples);
        if (!Object.keys(inner).length && !f.required) return;
        out[f.name] = this.isArrayField(f) ? [inner] : inner;
        return;
      }

      const raw = useExamples ? f.schema?.example : this.bodyValues[key];
      const hasVal = raw !== undefined && raw !== null && raw !== '';
      if (!f.required && !hasVal) return;
      out[f.name] = hasVal ? this.coerceBodyValue(raw, f) : (f.schema?.example ?? '');
    });
    return out;
  }

  buildRequestBody(endpoint: any, useExamples = false): any {
    if (!endpoint?.requestBodyFields?.length) return null;
    const body = this.collectBody(endpoint.requestBodyFields, '', useExamples);
    return Object.keys(body).length ? body : null;
  }

  filterParams(parameters: any[], type: string): any[] {
    return (parameters || []).filter((p: any) => p.in === type);
  }

  visibleBodyFields(endpoint: any): any[] {
    return (endpoint?.requestBodyFields || []).filter((f: any) => !f.hidden);
  }

  visibleNestBodyFields(parentFieldObj: any): any[] {
    return (parentFieldObj?.schema?.fields || []).filter((f: any) => !f.hidden);
  }

  isFieldFixed(field: any): boolean {
    // Pay10 Admin can test with different PAY_IDs, so keep PAY_ID editable for that role
    if (field?.name === 'PAY_ID' && this.role === Role.PAY10_ADMIN) {
      return false;
    }
    return !!field?.fixed;
  }

  buildCurlCommand(endpoint: any): string {
    const pathParams = this.filterParams(endpoint.parameters, 'path');
    const queryParams = this.filterParams(endpoint.parameters, 'query');
    const headerParams = this.filterParams(endpoint.parameters, 'header');
    const isMultipart = this.isMultipartEndpoint(endpoint);

    let path = endpoint.path;
    pathParams.forEach((p: any) => {
      const val = this.paramValues[p.name] || '{' + p.name + '}';
      path = path.replace('{' + p.name + '}', val);
    });

    const queryString = queryParams
      .filter((p: any) => p.required || this.paramValues[p.name])
      .map((p: any) => p.name + '=' + (this.paramValues[p.name] || ''))
      .join('&');

    const url = (endpoint.isFromPG ? API_EXP_SANDBOX_URL : WALLET_API_EXP_SANDBOX_URL) + path + (queryString ? '?' + queryString : '');
    const method = (endpoint.method as string).toUpperCase();

    const parts: string[] = [
      "curl --location '" + url + "'",
      "  --request " + method,
    ];
    if (!isMultipart) {
      parts.push("  --header 'Content-Type: application/json'");
    }

    headerParams.forEach((p: any) => {
      const isAuth = p.name.toLowerCase() === 'authorization';
      const userVal = this.paramValues[p.name];
      if (!isAuth && !p.required && !userVal) return;
      let val: string;
      if (isAuth) {
        val = userVal || (this.accessToken ? 'Bearer ' + this.accessToken : 'Bearer <access_token>');
      } else {
        val = userVal || '';
      }
      parts.push("  --header '" + p.name + ": " + val + "'");
    });

    if (endpoint.requestBodyFields?.length && ['POST', 'PUT', 'PATCH'].includes(method)) {
      if (isMultipart) {
        this.visibleBodyFields(endpoint).forEach((f: any) => {
          if (f.schema?.type === 'file' || f.schema?.format === 'binary') {
            const file = this.fileValues[f.name];
            parts.push("  --form '" + f.name + "=@/" + (file?.name || f.schema?.example || 'bulk_payout.xlsx') + "'");
          } else {
            const val = this.bodyValues[f.name];
            const hasVal = val !== undefined && val !== null && val !== '';
            if (f.required || hasVal) {
              parts.push("  --form '" + f.name + "=" + (hasVal ? val : '') + "'");
            }
          }
        });
      } else {
        const body: any = {};
        this.visibleBodyFields(endpoint).forEach((f: any) => {
          const val = this.bodyValues[f.name];
          const hasVal = val !== undefined && val !== null && val !== '';
          if (f.required || hasVal) body[f.name] = hasVal ? this.coerceBodyValue(val, f) : (val ?? '');
        });
        if (Object.keys(body).length) parts.push("  --data-raw '" + JSON.stringify(body, null, 2) + "'");
      }
    }

    return parts.join(' \\\n');
  }

  private buildRequestBodyExample(requestBody: any): any {
    const schema = requestBody?.content?.['application/json']?.schema;
    if (!schema) return {};
    const ref: string = schema['$ref'];
    if (ref) {
      const schemaName = ref.split('/').pop();
      const resolved = this.apiDetails?.schemas?.[schemaName!];
      return this.buildSchemaExample(resolved);
    }
    return this.buildSchemaExample(schema);
  }

  private buildSchemaExample(schema: any): any {
    if (!schema?.properties) return {};
    const example: any = {};
    Object.entries(schema.properties).forEach(([key, prop]: [string, any]) => {
      example[key] = prop.example ?? (prop.type === 'integer' ? 0 : prop.type === 'boolean' ? false : '');
    });
    return example;
  }

  get sandboxUrl(): string {
    const url = this.apiDetails?.endpoints?.[0]?.isFromPG ? API_EXP_SANDBOX_URL : WALLET_API_EXP_SANDBOX_URL;
    const path = this.apiDetails?.endpoints?.[0]?.path || '';
    return `${url}${path}`;
  }

  get productionUrl(): string {
    const url = this.apiDetails?.endpoints?.[0]?.isFromPG ? API_EXP_API_URL : WALLET_API_EXP_API_URL;
    const path = this.apiDetails?.endpoints?.[0]?.path || '';
    return `${url}${path}`;
  }

  formatJson(value: any): string {
    if (value === undefined || value === null) {
      return '';
    }
  
    try {
      return JSON.stringify(value, null, 2);
    } catch(error) {
      console.warn('formatJson failed: ', error);
      return '';
    }
  }

  onJsonInput(fieldName: string, value: string): void {
    try {
      this.bodyValues[fieldName] = JSON.parse(value);
    } catch(error) {}
  }

  resizeTextarea(el: HTMLTextAreaElement): void {
    const maxHeight = 400;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, maxHeight) + 'px';
  }

  ngAfterViewChecked(): void {
    this.autoTextareas?.forEach(ref => this.resizeTextarea(ref.nativeElement));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

    highlightResponseJson(value: any): SafeHtml {
    const json = JSON.stringify(value, null, 2);
    if (json === undefined) return '';
    const escaped = json
      .replace(/&/g, '&​amp;')
      .replace(/</g, '&​lt;')
      .replace(/>/g, '&​gt;');
    const highlighted = escaped.replace(
      /("(?:[^"\\]|\\.)*")(\s*:)?|(\btrue\b|\bfalse\b|\bnull\b)|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/g,
      (match: string, str: string, colon: string, literal: string, num: string) => {
        if (str) {
          if (colon) return `<span style="color: #C8D3F2 !important">${str}</span>${colon}`;
          return `<span style="color: #82C78E !important;">${str}</span>`;
        }
        if (literal) return `<span style="color: #FFFFFF !important;">${literal}</span>`;
        if (num) return `<span style="color: #FFFFFF !important;">${num}</span>`;
        return match;
      }
    );
    return this.sanitizer.bypassSecurityTrustHtml(highlighted);
  }

}